import ArrowLeft from "@assets/icons/arrow-left.svg";
import Arrow from "@assets/icons/back.svg";
import MenuIcon from "@assets/icons/menu-vertical.svg";
import { APP_CONSTANTS } from "@core/constants/app-constants";
import { useHistory } from "@core/routing/use-history";
import { useGoBack, useNavigate } from "@core/routing/use-navigate";
import { Patient, PendingPatient } from "@domain/patient/patient";
import { ProgramContent } from "@domain/program/program";
import { ProgramStatus } from "@domain/program/program-status/program-status";
import { e2eTestId } from "@e2e/test-ids";
import styled from "@emotion/styled";
import { ButtonIcon } from "@ui/components/buttons/button-icon";
import { DialogContext, ModalId } from "@ui/components/dialog/dialog-provider";
import { MenuButton, MenuItemProps } from "@ui/components/common/menu-button";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { RoutesPath } from "@ui/navigation/routes";
import { ButtonsContainerHeader } from "@ui/pages/program-builder/header/components/buttons-container-header";
import { PatientInformationsTooltip } from "@ui/pages/program-builder/header/components/patient-informations-tooltip";
import { ProgramDatesHeader } from "@ui/pages/program-builder/header/components/program-dates-header";
import { physioMeMobileBreakpoint, physioMeTabletBreakpoint, row, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { isNotNil } from "@utils/assert";
import { ButtonProps } from "primereact/button";
import { OnboardingProgramQuickstartStep } from "@domain/onboarding/onboarding";
import { classNames, IconType } from "primereact/utils";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router";

export interface HeaderEditorButtonProps {
	label: string;
	disabled?: boolean;
	loading?: boolean;
	tooltip?: string;
	icon?: IconType<ButtonProps>;
	onClick: () => void;
}

interface ProgramBuilderHeaderProps {
	patient?: Patient | PendingPatient;
	programContent: ProgramContent;
	programStatus?: ProgramStatus;
	statusMessage: string;
	secondaryButton?: HeaderEditorButtonProps;
	primaryButton?: HeaderEditorButtonProps;
	programActions: MenuItemProps[];
	onNameChanged: (name: string) => void;
	onProgramSchedulerClick?: () => void;
	editable: boolean;
	primaryQuickstartTooltipVisible?: boolean;
	onPrimaryQuickstartTooltipRequestClose?: () => void;
	primaryQuickstartTooltipStep?:
		| OnboardingProgramQuickstartStep.START_TEMPLATE_GUIDE_01
		| OnboardingProgramQuickstartStep.START_DRAFT_GUIDE_01;
}

export const ProgramBuilderHeader: React.FC<ProgramBuilderHeaderProps> = (props: ProgramBuilderHeaderProps) => {
	const {
		patient,
		programStatus,
		programContent,
		statusMessage,
		primaryButton,
		secondaryButton,
		programActions,
		onNameChanged,
		onProgramSchedulerClick,
		editable,
		primaryQuickstartTooltipVisible,
		onPrimaryQuickstartTooltipRequestClose,
		primaryQuickstartTooltipStep,
	} = props;
	const [editing, setEditing] = useState(false);
	const [title, setTitle] = useState(programContent.name);
	const { isDesktop } = useDimensions();
	const dialogContext = useContext(DialogContext);
	const goBack = useGoBack();
	const navigate = useNavigate();
	const location = useLocation();
	const { isInitialLocation } = useHistory();

	useEffect(() => {
		setTitle(programContent.name);
	}, [programContent.name]);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		switch (event.key) {
			case "Enter":
				onNameChanged(title);
				setEditing(false);
				break;
			case "Escape":
				setTitle(programContent.name);
				setEditing(false);
				break;
			default:
				break;
		}
	};

	const onGoBack = useCallback(() => {
		if (isInitialLocation) {
			navigate(RoutesPath.Home);
		} else {
			location.pathname.startsWith(RoutesPath.ProgramBuilder.split(":")[0])
				? navigate(RoutesPath.ProgramList)
				: goBack();
		}
	}, [goBack, isInitialLocation, navigate, location]);

	return (
		<Header>
			<LeftContainer>
				<ButtonIcon
					icon={isDesktop ? ArrowLeft : Arrow}
					onClick={onGoBack}
					size="condensed"
					testId={e2eTestId.createTemplatePage.headerBackButton}
				/>
				{isDesktop && (
					<Stack>
						<Top>
							<TitleContainer editing={editing} onClick={() => editable && !editing && setEditing(true)}>
								{editing ? (
									<Input
										className={classNames("p-size-l-bold flex-auto")}
										maxLength={APP_CONSTANTS.PROGRAM_OR_TEMPLATE_MAX_TITLE_LENGTH}
										type="text"
										value={title}
										autoFocus
										onBlur={() => {
											onNameChanged(title);
											setEditing(false);
										}}
										onChange={e => {
											setTitle(e.target.value);
										}}
										onKeyDown={handleKeyDown}
									/>
								) : (
									<TextOverflow data-test={e2eTestId.patientPage.titreTextInput} className="p-size-l-bold">
										{programContent.name}
									</TextOverflow>
								)}
							</TitleContainer>
							{programActions.length > 0 ? (
								<StyledMenuButton menuItems={programActions} />
							) : (
								programStatus === ProgramStatus.Draft && <Separator />
							)}
							<span className="p-size-s">{statusMessage}</span>
						</Top>
						{isNotNil(patient) && (
							<Bottom>
								<PatientInformationsTooltip patient={patient} />
							</Bottom>
						)}
					</Stack>
				)}
			</LeftContainer>
			<MiddleContainer>
				{programStatus === ProgramStatus.Draft || programStatus === undefined ? (
					!isDesktop && <TextOverflow className="p-size-s-bold">{programContent.name}</TextOverflow>
				) : (
					<ProgramDatesHeader
						programContent={programContent}
						programStatus={programStatus}
						onClick={onProgramSchedulerClick}
					/>
				)}
			</MiddleContainer>
			<RightContainer>
				{isDesktop ? (
					(secondaryButton || primaryButton) && (
						<ButtonsContainerHeader
							secondaryButton={secondaryButton}
							primaryButton={primaryButton}
							primaryQuickstartTooltipVisible={primaryQuickstartTooltipVisible}
							onPrimaryQuickstartTooltipRequestClose={onPrimaryQuickstartTooltipRequestClose}
							primaryQuickstartTooltipStep={primaryQuickstartTooltipStep}
						/>
					)
				) : (
					<ButtonIcon
						icon={MenuIcon}
						onClick={() => {
							dialogContext?.openModal(ModalId.ProgramBuilderActions, {
								programName: programContent.name,
								statusMessage,
								onNameChanged: (newName: string) => {
									setTitle(newName);
									onNameChanged(newName);
									setEditing(false);
								},
								programActions,
								patient,
							});
						}}
						size="condensed"
					/>
				)}
			</RightContainer>
		</Header>
	);
};

const Header = styled.div`
	position: sticky;
	top: 0;
	z-index: 22;
	${row(0, "spacebetween", "center")}
	padding: 14px 20px;
	background-color: ${colors.white};
	border-bottom: 1px solid ${colors.primary25};

	@media ${physioMeMobileBreakpoint} {
		padding: 16px 8px;
	}
`;

const LeftContainer = styled.div`
	${row(12, "flexstart", "center")};
	flex: 1 1 0;
`;

const MiddleContainer = styled.div`
	${row(undefined, "center", "center")};
	flex: 1 1 0;
`;

const RightContainer = styled.div`
	${row(undefined, "flexend", "center")};
	flex: 1 1 0;
`;

const Stack = styled.div`
	${stack(undefined, "center", "flexstart")}
`;

const Top = styled.div`
	${row(undefined, "flexstart", "center")}
	height: 40px;
`;
const Bottom = styled.div`
	${row(undefined, "flexstart", "center")}
`;

const TitleContainer = styled.div<{ editing: boolean }>`
	${row(undefined, "flexend", "center")};
	min-width: 0;
	overflow: hidden;
	max-width: 500px;
	cursor: ${props => (props.editing ? undefined : "text")};
`;

const Separator = styled.div`
	width: 1px;
	height: 24px;
	margin-right: 8px;
	margin-left: 8px;
	background-color: ${colors.primary25};
`;

const TextOverflow = styled.span`
	max-width: 300px;
	line-height: 1.375rem;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
	@media ${physioMeTabletBreakpoint} {
		max-width: 250px;
	}
`;

const Input = styled.input`
	max-width: 300px;
	height: 1.375rem;
	appearance: none;
	padding: 0;
	border: none;
	text-overflow: ellipsis;
	background-color: ${colors.primaryBackground};
	color: ${colors.black};

	&:focus {
		outline: 0;
	}
`;

const StyledMenuButton = styled(MenuButton)`
	.menu-button {
		background-color: ${colors.transparent};

		&.hover {
			background-color: ${colors.grey10};
		}
	}
`;
